import React, { useEffect } from 'react';
import {useLocation} from "react-router-dom";
import ReactModal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import { taskApi } from '../../../apis/task/taskApi';
import { roommemberApi } from '../../../apis/roommember/roommemberApi';
import icon442 from '../../../assets/images/ROOM_TaskBoard_Re4_42.png';
import icon05 from '../../../assets/images/ROOM_TaskFile_Re_05.png';
import { RoomMember, Task } from '../../../types/types';
import useLoading from '../../../hooks/useLoading';
import {logError} from "../../../libs/utils/logError";
import useAppRouter from "../../../hooks/useAppRouter";

interface UpdateTaskAssignUserModalProps {
  onSubmitted: () => Promise<void>;
  task: Task;
}

export const UpdateTaskAssignUserModal = (
  props: UpdateTaskAssignUserModalProps
) => {
  const withLoading = useLoading();
  const location = useLocation();
  const { goto } = useAppRouter();
  const { task, onSubmitted } = props;
  const { success } = useSystemMessage();
  const [roomMembers, setRoomMembers] = React.useState<RoomMember[]>([]);
  const [assignUserId, setAssignUserId] = React.useState<number | null>(null);
  const [disabled, setDisabled] = React.useState<boolean>(false);

  useEffect(() => {
    const fetch = async () => {
      withLoading(async () => {
        try {
          const response = await roommemberApi.index({});
          setRoomMembers(response.memberList);
        } catch(e) {
          await logError(
            location.pathname,
            '担当者一覧の取得に失敗しました。',
            e
          );

          goto('internalServerError');
        }
      });
    };
    fetch();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [task]);

  useEffect(() => {
    if (!roomMembers.length) {
      return;
    }
    const taskAssignUserId = roomMembers.find(
      (rm) => rm.name === task?.assignUser
    )?.id;
    if (taskAssignUserId !== undefined) {
      setAssignUserId(taskAssignUserId);
    }
  }, [task, roomMembers]);

  const handleSubmit = async () => {
    setDisabled(true);
    try {
      await taskApi.update(task.id, { name: task.name, assignUserId }, {});
      setDisabled(false);
      await onSubmitted();
      await success('担当者を保存しました。');
    } catch(e) {
      await logError(
        location.pathname,
        '担当者の保存に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => onSubmitted()}
        className="content"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">担当者指定</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => onSubmitted()}
            >
              <img src={icon05} alt="icon" />
            </button>
          </div>
          <div className="px-4 pt-8">
            <div className="px-2 grid grid-cols-2 gap-4">
              {assignUserId === null ? (
                <label htmlFor="assign-user" className="btn-radio-pill-checked">
                  担当者指定なし
                  <input
                    id="assign-user"
                    type="radio"
                    className="hidden"
                    checked
                    onChange={() => setAssignUserId(null)}
                  />
                </label>
              ) : (
                <label htmlFor="assign-user" className="btn-radio-pill">
                  担当者指定なし
                  <input
                    id="assign-user"
                    type="radio"
                    className="hidden"
                    checked={false}
                    onChange={() => setAssignUserId(null)}
                  />
                </label>
              )}
            </div>
            <div className="border border-primary my-8 border-opacity-50" />
            <div className="px-2 grid grid-cols-2 gap-4">
              {roomMembers.map((rm) =>
                assignUserId === rm.id ? (
                  <label
                    key={rm.id}
                    htmlFor={`assign-user${rm.id}`}
                    className="flex"
                  >
                    <div className="btn-radio-rounded-checked">
                      <img src={icon442} alt="icon" />
                      <div className="ml-4">{rm.name}</div>
                    </div>
                    <input
                      id={`assign-user${rm.id}`}
                      type="radio"
                      className="hidden"
                      checked
                      onChange={() => setAssignUserId(rm.id)}
                    />
                  </label>
                ) : (
                  <label
                    key={rm.id}
                    htmlFor={`assign-user${rm.id}`}
                    className="flex"
                  >
                    <div className="btn-radio-rounded">
                      <img src={icon442} alt="icon" />
                      <div className="ml-4">{rm.name}</div>
                    </div>
                    <input
                      id={`assign-user${rm.id}`}
                      type="radio"
                      className="hidden"
                      value={rm.id}
                      checked={rm.id === assignUserId}
                      onChange={() => setAssignUserId(rm.id)}
                    />
                  </label>
                )
              )}
            </div>
            <div className="border border-primary mt-16 border-opacity-50" />
          </div>
          <div className="form-modal-footer-container">
            <div className="form-modal-footer">
              <button
                className="btn-sub"
                type="button"
                onClick={() => onSubmitted()}
              >
                キャンセル
              </button>
              <button
                className="btn-primary"
                type="button"
                onClick={handleSubmit}
                disabled={disabled}
              >
                {disabled ? (
                  <>
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="animate-spin mr-4"
                    />
                    保 存
                  </>
                ) : (
                  '保　存'
                )}
              </button>
            </div>
          </div>
        </div>
      </ReactModal>
    </>
  );
};
